/**
 * №13.1 (§1.2) — the contextual-action registry, consumed by the chrome.
 *
 * One hook over the `list_context_actions` verb (№11.1.1): given a context entity
 * type and (optionally) a record id, the server resolves which installed modules'
 * actions apply, binds their prefill against the record, and marks the bound args
 * LOCKED. The App Shell's record-action bar, related-list add-actions and command
 * strip all read THIS — no per-page prefill wiring (the §1.2 binding rule).
 */
import { useQuery } from "@tanstack/react-query";
import { mcpClient } from "../../lib/mcp-client";

/** Where an action is allowed to render. Mirrors the server ActionPlacement. */
export type ActionPlacement = "record_actions" | "command_strip" | `related:${string}`;

export interface ResolvedAction {
  verb: string;
  context: string;
  label: string;
  icon?: string;
  placement: ActionPlacement[];
  /** Args to hand the verb/create form, resolved from the record. */
  prefill: Record<string, unknown>;
  /** Keys in `prefill` that are locked (visible, not editable). */
  locked: string[];
  /** Provenance — the module that declared this action. */
  module: string;
  /** №13.3 §1 — completion contract. `open` routes to the new record; `stay`
   *  keeps the originating record in view + lands a confirmation toast.
   *  Undefined → the chrome defaults (create-shaped → open, else stay). */
  on_success?: "open" | "stay";
  /** Set when the record could not be read (prefill left unresolved). */
  unresolved?: boolean;
}

interface ContextActionsResult {
  context: string;
  id: string | null;
  actions: ResolvedAction[];
}

/**
 * Resolve the contextual actions for a `context` entity type (optionally a
 * specific record whose fields bind the prefill). Returns the full set; callers
 * filter by placement (record_actions / related:<type> / command_strip).
 */
export function useContextActions(context: string | undefined, id?: string) {
  return useQuery({
    queryKey: ["context-actions", context, id ?? null],
    enabled: !!context,
    staleTime: 30_000,
    queryFn: async (): Promise<ContextActionsResult> => {
      const r = await mcpClient.toolsCall<ContextActionsResult>("list_context_actions", {
        context,
        ...(id ? { id } : {}),
      });
      if (r.isError) throw new Error(r.content?.[0]?.text ?? "list_context_actions failed");
      return (r.structuredContent as ContextActionsResult) ?? { context: context!, id: id ?? null, actions: [] };
    },
  });
}

/** Filter a resolved set to one placement (record_actions by default). */
export function atPlacement(actions: ResolvedAction[] | undefined, placement: ActionPlacement): ResolvedAction[] {
  return (actions ?? []).filter((a) => a.placement.includes(placement));
}

/** Filter to a related-list add action for a given target entity type. */
export function relatedActionFor(actions: ResolvedAction[] | undefined, targetSlug: string): ResolvedAction | undefined {
  return (actions ?? []).find((a) => a.placement.includes(`related:${targetSlug}` as ActionPlacement));
}
